import React, { useState, useEffect, useCallback, useRef, useMemo  } from "react";
import { useParams } from "react-router-dom";
import TicketList from "./TicketList";
import ChatCanvas from "./ChatCanvas";
import TicketDetails from "./TicketDetails";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as signalR from "@microsoft/signalr";
import {
  useGetChatBotAllConversationsQuery,
  useLazyGetChatBotMessagesQuery,
  usePostSendChatBotMessageMutation,
  usePostEndChatBotMutation,
} from "../../State/Api/ChatBotApiSlice";


function SupportHandling() {
  const { conversationID } = useParams();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [connection, setConnection] = useState(null);
  const [isChatEnded, setIsChatEnded] = useState(false);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);

  const connectionRef = useRef(null);

    const {
    data: conversations = [], isLoading, } = useGetChatBotAllConversationsQuery();
 const [ getMessages, { isFetching: loadingMessages, isError: fetchError } ] = useLazyGetChatBotMessagesQuery();
  const [sendMessageApi] = usePostSendChatBotMessageMutation();
  const [endChat] = usePostEndChatBotMutation();

 const setupConnection = useCallback(async (convID) => {
    if (!convID || connectionRef.current) return;

    const conn = new signalR.HubConnectionBuilder()
      .withUrl("https://api.pelicanrx.com/chatHub")
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    await conn.start();
    console.log("✅ SignalR connected");
    await conn.invoke("JoinConversation", parseInt(convID, 10));

    conn.on("ReceiveMessage", (msg) => {
      const hasFile = Array.isArray(msg.files) && msg.files.length > 0;
      const file = hasFile ? msg.files[0] : null;

      setMessages((prev) => {
        const exists = prev.some(
          (m) =>
            m.id === msg.messageID ||
            (m.text === msg.message &&
              m.sender === (msg.isAdmin ? "admin" : "user"))
        );
        if (exists) return prev;

        return [
          ...prev,
          {
            id: msg.messageID ?? Date.now() + Math.random(),
            text: msg.message ?? msg.text ?? "",
            sender: msg.isAdmin ? "admin" : "user",
            timestamp: msg.timestamp ?? new Date().toISOString(),
            previewImage:
              hasFile && file.fileType?.startsWith("image/")
                ? file.fileUrl
                : null,
            fileUrl: hasFile ? file.fileUrl : null,
            fileName: hasFile ? file.fileName : null,
          },
        ];
      });
    });

    connectionRef.current = conn;
  }, []);

const loadMessages = useCallback(
    async (convID) => {
      if (!convID) return;
      try {
        const res = await getMessages(convID).unwrap();
        const formatted = res.map((m) => {
          const isAdmin =
            m.isAdminResponse === true ||
            m.isAdmin === true ||
            m.sender?.toLowerCase() === "admin";

          const hasFiles = Array.isArray(m.files) && m.files.length > 0;
          const firstFile = hasFiles ? m.files[0] : null;

          return {
            id: m.messageID,
            text: m.message,
            sender: isAdmin ? "admin" : "user",
            timestamp: m.createdAt ?? m.timestamp,
            hasAttachment: hasFiles,
            attachmentType: hasFiles ? "file" : null,
            fileUrl: firstFile?.fileUrl || null,
            fileName: firstFile?.fileName || null,
            files: m.files || [],
          };
        });

        setMessages(formatted);
      } catch (err) {
        console.error("❌ Error loading messages:", err);
      }
    },
    [getMessages]
  );


  const handleSendMessage = async (input) => {
    if (!selectedTicket) return;
    const convID = selectedTicket.conversationID;

    const isFileObject = input instanceof File || input?.file instanceof File;
    const text = typeof input === "string" ? input : input?.text || "";
    const file = isFileObject ? (input.file || input) : null;
    const previewImage = input?.previewImage || null;

    const tempMessage = {
      id: `temp-${Date.now()}`,
      text: isFileObject ? `📎 ${file.name}` : text,
      sender: "admin",
      timestamp: new Date().toISOString(),
      isTemp: true,
      hasAttachment: isFileObject,
      attachmentType: isFileObject
        ? file.type.startsWith("image/") ? "image" : "file"
        : null,
      previewImage,
      fileName: file?.name,
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const formData = new FormData();
      formData.append("ConversationID", convID);
      formData.append("IsAdmin", true);
      formData.append("Message", text || (file ? `📎 ${file.name}` : "Attachment"));
      if (file) formData.append("File", file);

      const response = await sendMessageApi(formData).unwrap();
      console.log("✅ Sent successfully:", response);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempMessage.id
            ? {
                ...m,
                isTemp: false,
                fileUrl: response?.fileUrl || null,
                text: response?.message || m.text,
              }
            : m
        )
      );
    } catch (err) {
      console.error("❌ Failed to send message:", err);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempMessage.id ? { ...m, failed: true } : m
        )
      );
    }
  };

 useEffect(() => {
    if (conversationID && conversations.length) {
      const found = conversations.find(
        (c) => c.conversationID === parseInt(conversationID, 10)
      );
      if (found) {
        setSelectedTicket(found);
        loadMessages(found.conversationID);
        setupConnection(found.conversationID);
      }
    }
  }, [conversationID, conversations, loadMessages, setupConnection]);

  useEffect(() => {
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, []);

  const toggleRightPanel = () => setIsRightPanelOpen((p) => !p);

const handleStatusChange = (ticketId, newStatus) => {
    setSelectedTicket((prev) =>
      prev && prev.id === ticketId ? { ...prev, status: newStatus } : prev
    );
  };

 const handleTicketSelect = async (ticket) => {
    if (!ticket) return;

    if (connectionRef.current) {
      try {
        await connectionRef.current.stop();
        console.log("🛑 Previous SignalR connection closed");
      } catch (e) {
        console.warn("SignalR cleanup error:", e);
      }
      connectionRef.current = null;
    }

    setSelectedTicket(ticket);
    setMessages([]);
    await loadMessages(ticket.conversationID);
    await setupConnection(ticket.conversationID);
  };


const filesFromMessages = useMemo(() => {
    const out = [];
    const seen = new Set();

    const getType = (name = "") => {
      const lower = name.toLowerCase();
      if (/\.(jpg|jpeg|png|gif|bmp|webp)$/.test(lower)) return "image";
      if (/\.(pdf)$/.test(lower)) return "pdf";
      if (/\.(doc|docx)$/.test(lower)) return "document";
      if (/\.(xls|xlsx|csv)$/.test(lower)) return "spreadsheet";
      return "file";
    };

    for (const m of messages || []) {
      const files = Array.isArray(m.files) ? m.files : [];
      for (const f of files) {
        const uploadedAt = f.uploadedAt || m.timestamp || m.createdAt || null;
        const key = `${f.fileUrl}|${f.fileName}|${uploadedAt}`;
        if (seen.has(key)) continue;
        seen.add(key);

        out.push({
          id: f.fileID || `${m.messageID}-${f.fileName}`,
          name: f.fileName || "Attachment",
          url: f.fileUrl || "",
          uploadedAt,
          sharedDate: uploadedAt ? new Date(uploadedAt).toLocaleString() : "",
          type: getType(f.fileName),
          agent:
            m.isAdminResponse === true ||
            m.sender?.toLowerCase() === "admin"
              ? "Agent"
              : "User",
          size: f.size || "",
        });
      }
    }

    out.sort(
      (a, b) =>
        new Date(b.uploadedAt || 0).getTime() -
        new Date(a.uploadedAt || 0).getTime()
    );
    return out;
  }, [messages]);

  const SupportHandlingStyles = `
    #dash-con {
      padding-top: 0px !important;
    }

    .support-handling-container {
      width: 100%;
      height: 91vh;
      background-color: #f8f9fa;
      overflow: hidden;
      position: relative;
    }

    .support-layout {
      display: flex;
      height: 100%;
      gap: 1px;
      background-color: #e5e7eb;
      position: relative;
    }

    .left-panel {
      width: 320px;
      background-color: white;
      border-right: 1px solid #e5e7eb;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
    }

    .middle-panel {
      flex: 1;
      background-color: white;
      display: flex;
      flex-direction: column;
      position: relative;
      transition: margin-right 0.3s ease;
      margin-right: ${isRightPanelOpen ? "0" : "0"};
    }

    .right-panel {
      width: ${isRightPanelOpen ? "300px" : "0px"};
      background-color: white;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: width 0.3s ease;
      border-left: ${isRightPanelOpen ? "1px solid #e5e7eb" : "none"};
    }

    .panel-toggle {
      position: absolute;
      top: 50%;
      right: ${isRightPanelOpen ? "300px" : "0px"};
      transform: translateY(-50%);
      width: 24px;
      height: 48px;
      background: white;
      border: 1px solid #e5e7eb;
      border-right: ${isRightPanelOpen ? "1px solid #e5e7eb" : "none"};
      border-left: ${isRightPanelOpen ? "none" : "1px solid #e5e7eb"};
      border-radius: ${isRightPanelOpen ? "6px 0 0 6px" : "0 6px 6px 0"};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      transition: all 0.3s ease;
      box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
    }

    .panel-toggle:hover {
      background-color: #f9fafb;
      box-shadow: -3px 0 6px rgba(0, 0, 0, 0.15);
    }

    .panel-toggle-icon {
      color: #6b7280;
      transition: color 0.15s ease;
    }

    .panel-toggle:hover .panel-toggle-icon {
      color: #374151;
    }

    @media (max-width: 1200px) {
      .right-panel {
        width: ${isRightPanelOpen ? "280px" : "0px"};
      }
      .left-panel {
        width: 300px;
      }
      .panel-toggle {
        right: ${isRightPanelOpen ? "280px" : "0px"};
      }
    }

    @media (max-width: 768px) {
      .support-layout {
        flex-direction: column;
      }
      
      .left-panel {
        width: 100%;
      }
      
      .right-panel {
        width: 100%;
        height: ${isRightPanelOpen ? "auto" : "0px"};
      }
      
      .panel-toggle {
        display: none;
      }
    }
  `;

  return (
    <>
      <style>{SupportHandlingStyles}</style>
      <div className="support-handling-container">
        <div className="support-layout">
          {/* Left Panel - Support Tickets List */}
          <div className="left-panel">
            <TicketList
              conversations={conversations}
              selectedTicket={selectedTicket}
              onTicketSelect={handleTicketSelect}
              isLoading={isLoading}
            />
          </div>

          {/* Middle Panel - Chat Canvas */}
          <div className="middle-panel">
            <ChatCanvas
              selectedTicket={selectedTicket}
              messages={messages}
              onStatusChange={handleStatusChange}
              onSendMessage={handleSendMessage}
               onEndChat={endChat}
               onChatEnded={() => setIsChatEnded(true)}
               loadingMessages={loadingMessages}
            />
          </div>

          {/* Right Panel Toggle Button */}
          <div className="panel-toggle" onClick={toggleRightPanel}>
            <div className="panel-toggle-icon">
              {isRightPanelOpen ? (
                <ChevronRight size={16} />
              ) : (
                <ChevronLeft size={16} />
              )}
            </div>
          </div>

          {/* Right Panel - Ticket Details */}
          <div className="right-panel">
            {isRightPanelOpen && (
              <TicketDetails
                selectedTicket={selectedTicket}
                conversations={conversations}
                 files={filesFromMessages}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SupportHandling;
